/* eslint-disable indent */
import { NavLink } from 'react-router-dom'

import i18n, { t } from 'i18next'

import { LogoStore } from '~/components/LogoStore'
import { ConversionRateCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionRateCutoffAlertIcon'
import { ConversionsCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionsCutoffAlertIcon'
import { RoasMetricCell } from '~/modules/retailMedia/components/RoasMetricCell'
import { formatRoasCutoffDate } from '~/modules/retailMedia/constants'
import {
  buildNtbColumns,
  buildNtbTooltipValues
} from '~/modules/retailMedia/helpers/ntbMetrics'

import { ImpressionShareMetricCell } from '../_elements/ImpressionShareMetricCell'
import {
  haloColumns,
  buildClickAttributionColumns
} from '../_elements/roasColumns'
import { MetricTooltip } from '../../MetricMethodologyTooltip'

type SchemaProps = {
  availableMetrics?: NewMetricsAvailability
  showClickAlert?: boolean
  lookbackMonths?: number
  ntbOrdersWindow?: NtbOrdersWindow
}

export const schema = ({
  availableMetrics = {
    impressionShare: false,
    lostIsRank: false,
    lostIsBudget: false,
    newToBrandCustomers: false,
    newToBrandRevenue: false
  },
  showClickAlert = false,
  lookbackMonths,
  ntbOrdersWindow
}: SchemaProps = {}) => {
  /**
   * Colunas de atribuição (Halo + ROAS por clique) são compartilhadas e tipadas
   * para AdsResultFormatted. Adaptamos ao tipo da tabela de publishers para
   * referenciá-las individualmente na ordem desejada.
   */
  const attributionColumns = {
    ...haloColumns,
    ...buildClickAttributionColumns(showClickAlert)
  } as unknown as TableSchema<AdvertiserWithMetrics>

  return {
    publisher: {
      title: 'Publisher',
      sortKey: 'name',
      render: rowData => (
        <NavLink
          to={`/retail-media/campaigns?rmid=${rowData.id}`}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <LogoStore
            alt={rowData.title}
            linkLogo={rowData.logo_url}
            backgroundLogo={
              rowData.account_theme?.header_color || 'transparent'
            }
            size="medium"
          />
          <div style={{ lineHeight: 1, marginLeft: '.5rem' }}>
            <strong className="fs-6">{rowData.title}</strong>
          </div>
        </NavLink>
      ),
      className: 'order__details col__infos col__title',
      showTotal: true
    },
    balance: {
      title: t('rm:balanceInWallet'),
      sortKey: 'balance',
      sortInitialToggleDirection: 'DESC',
      render: rowData => (
        <div style={{ lineHeight: 1.125 }}>
          {rowData?.balance}
          {rowData?.checkingAccountId && (
            <>
              <br />
              <NavLink
                to={`/retail-media/wallet/${rowData.checkingAccountId}/movimentations?rmid=${rowData.id}`}
              >
                <small>{t('rm:accessWallet')}</small>
              </NavLink>
            </>
          )}
        </div>
      )
    },
    total_campaigns: {
      sortKey: 'total_campaigns',
      sortInitialToggleDirection: 'DESC',
      title: t('rm:Campaigns'),
      optional: true,
      render: ({ metrics }) => metrics.totalCampaigns
    },
    impressions: {
      sortKey: 'impressions',
      sortInitialToggleDirection: 'DESC',
      title: t('rm:metrics.impressions'),
      helpText: t('rm:metrics.impressions.tooltip'),
      render: ({ metrics }) => metrics.impressions
    },
    views: {
      sortKey: 'views',
      sortInitialToggleDirection: 'DESC',
      title: t('rm:metrics.views'),
      helpText: t('rm:metrics.views.tooltip'),
      render: ({ metrics }) => metrics.views
    },
    clicks: {
      sortKey: 'clicks',
      sortInitialToggleDirection: 'DESC',
      title: t('rm:metrics.clicks'),
      helpText: t('rm:metrics.clicks.tooltip'),
      render: ({ metrics }) => metrics.clicks
    },
    ctr: {
      title: t('rm:metrics.ctr'),
      helpContent: <MetricTooltip metric="ctr" />,
      optional: true,
      sortKey: 'ctr',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => metrics.ctr
    },
    totalSpent: {
      sortKey: 'total_spent',
      sortInitialToggleDirection: 'DESC',
      title: t('rm:metrics.investment'),
      helpText: t('rm:metrics.investment.tooltip'),
      render: ({ metrics }) => metrics.totalSpent
    },
    conversion: {
      sortKey: 'conversions',
      sortInitialToggleDirection: 'DESC',
      title: t('rm:metrics.conversions'),
      headerSuffix: showClickAlert ? <ConversionsCutoffAlertIcon /> : undefined,
      helpText: t('rm:metrics.conversions.tooltip'),
      render: ({ metrics }) => metrics.conversions
    },
    conversionsClick: {
      ...attributionColumns.conversionsClick,
      optional: true,
      helpContent: <MetricTooltip metric="conversionsClick" />
    },
    conversion_rate: {
      sortKey: 'conversion_rate',
      sortInitialToggleDirection: 'DESC',
      title: t('rm:metrics.conversion_rate'),
      headerSuffix: showClickAlert ? (
        <ConversionRateCutoffAlertIcon />
      ) : undefined,
      helpContent: (
        <MetricTooltip
          metric="conversion_rate"
          values={{ date: formatRoasCutoffDate(i18n.language) }}
          learnMoreHref={t('rm:alerts.conversionUpdate.link')}
        />
      ),
      render: ({ metrics }) => metrics.conversionRate
    },
    conversionRateClick: {
      ...attributionColumns.conversionRateClick,
      optional: true,
      helpContent: <MetricTooltip metric="conversionRateClick" />
    },
    income: {
      sortKey: 'income',
      sortInitialToggleDirection: 'DESC',
      title: t('rm:metrics.sales'),
      helpText: t('rm:metrics.sales.tooltip'),
      render: ({ metrics }) => metrics.income
    },
    salesClick: {
      ...attributionColumns.salesClick,
      optional: true,
      helpText: t('rm:metrics.salesClick.tooltip')
    },
    roas: {
      title: t('rm:metrics.roas'),
      helpContent: (
        <MetricTooltip
          metric="roas"
          values={{ date: formatRoasCutoffDate(i18n.language) }}
          learnMoreHref={t('rm:alerts.roasUpdate.link')}
        />
      ),
      sortKey: 'roas',
      sortInitialToggleDirection: 'DESC',
      render: ({ metrics }) => (
        <RoasMetricCell metric="roas" value={metrics.roas} raw={metrics.raw} />
      )
    },
    roasClick: {
      ...attributionColumns.roasClick,
      optional: true,
      helpContent: <MetricTooltip metric="roasClick" />
    },
    roasHalo: {
      ...attributionColumns.roasHalo,
      optional: true,
      helpContent: <MetricTooltip metric="roasHalo" />
    },
    roasOverall: {
      ...attributionColumns.roasOverall,
      optional: true,
      helpContent: <MetricTooltip metric="roasOverall" />
    },
    haloOrders: {
      ...attributionColumns.haloOrders,
      optional: true,
      helpContent: <MetricTooltip metric="haloOrders" />
    },
    haloRevenue: {
      ...attributionColumns.haloRevenue,
      optional: true,
      helpContent: <MetricTooltip metric="haloRevenue" />
    },
    haloItems: {
      ...attributionColumns.haloItems,
      optional: true,
      helpContent: <MetricTooltip metric="haloItems" />
    },
    ...(availableMetrics.impressionShare
      ? {
          impressionShare: {
            sortKey: 'impression_share',
            sortInitialToggleDirection: 'DESC',
            title: t('rm:metrics.impressionShare'),
            helpContent: <MetricTooltip metric="impressionShare" />,
            helpText: t('rm:metrics.impressionShare.tooltip'),
            optional: true,
            render: ({ metrics }) => (
              <ImpressionShareMetricCell
                value={metrics?.impressionShare}
                raw={metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...(availableMetrics.lostIsRank
      ? {
          lostIsRank: {
            sortKey: 'lost_is_rank',
            sortInitialToggleDirection: 'DESC',
            title: t('rm:metrics.lostIsRank'),
            helpContent: <MetricTooltip metric="lostIsRank" />,
            helpText: t('rm:metrics.lostIsRank.tooltip'),
            optional: true,
            render: ({ metrics }) => (
              <ImpressionShareMetricCell
                value={metrics?.lostIsRank}
                raw={metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...(availableMetrics.lostIsBudget
      ? {
          lostIsBudget: {
            sortKey: 'lost_is_budget',
            sortInitialToggleDirection: 'DESC',
            title: t('rm:metrics.lostIsBudget'),
            helpContent: <MetricTooltip metric="lostIsBudget" />,
            helpText: t('rm:metrics.lostIsBudget.tooltip'),
            optional: true,
            render: ({ metrics }) => (
              <ImpressionShareMetricCell
                value={metrics?.lostIsBudget}
                raw={metrics?.raw}
              />
            )
          }
        }
      : {}),
    ...buildNtbColumns<AdvertiserWithMetrics>({
      availableMetrics,
      ntbValues: buildNtbTooltipValues({
        lookbackMonths,
        ordersWindow: ntbOrdersWindow
      }),
      render:
        metric =>
        ({ metrics }) =>
          metrics?.[metric.key]
    })
  } satisfies TableSchema<AdvertiserWithMetrics>
}
